// Selection frames for DOCUMENT content: images, vector
// fills, marquee regions, the open text-edit's move/scale handles, and
// AcroForm widget overlays.
import { memo, useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import type {
  FillInfo,
  RegionItem,
  WidgetInfo,
} from "../lib/protocol.ts";
import type { DragPhase } from "../lib/ui-types.ts";
import type { ActiveTextEdit } from "../lib/ui-types.ts";

/** Optional drag-snapping hook: receives the moved bbox + phase and
 *  returns the correction to apply (PageView owns targets + guides). */
export type ContentSnap = (
  bbox: [number, number, number, number],
  phase: DragPhase
) => { dx: number; dy: number };
import { usePointerDrag } from "./annot-views.tsx";
import { HtmlBoxDims } from "./dim-labels.tsx";
import {
  boxChanged,
  resizeFree,
  resizeLocked,
  type Box,
  type Corner,
} from "../lib/corner-resize.ts";

/** Every corner, in a stable order — three views render the same set. */
const ALL_CORNERS: readonly Corner[] = ["nw", "ne", "sw", "se"];
import { ColorPicker } from "./ColorPicker.tsx";

/** A modifier+click on a selection frame is a multi-select gesture, not
 *  the start of a drag: return false and the press bubbles to the page,
 *  which toggles whatever object is under the pointer. */
function isMultiSelectClick(e: {
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}): boolean {
  return e.ctrlKey || e.metaKey || e.shiftKey;
}

/** A selected content image: dashed frame with drag-to-move,
 *  an aspect-locked corner resize, and a Replace/Delete mini toolbar. */
/** One corner grip. A component rather than four copies of the same
 *  pointer plumbing, and it keeps `usePointerDrag`'s cancel handling
 *  (a touch the browser reclaims must roll the resize back, not commit
 *  half of it). */
function CornerHandle({
  corner,
  scale,
  onResize,
  title = "Resize",
}: {
  corner: "nw" | "ne" | "sw" | "se";
  scale: number;
  onResize: (dx: number, dy: number, phase: DragPhase) => void;
  title?: string;
}) {
  const drag = usePointerDrag(scale, onResize);
  return (
    <div
      className="pe-stamp-handle"
      data-corner={corner}
      title={title}
      onPointerDown={drag.onPointerDown}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
    />
  );
}

export const ImageSelView = memo(function ImageSelView({
  bbox,
  scale,
  busy,
  snap,
  onMove,
  onDelete,
  onReplace,
}: {
  bbox: [number, number, number, number];
  scale: number;
  busy: boolean;
  snap?: ContentSnap;
  onMove: (to: [number, number, number, number]) => void;
  onDelete: () => void;
  onReplace: () => void;
}) {
  const [x0, y0, x1, y1] = bbox;
  const w = x1 - x0;
  const h = y1 - y0;
  const [dOff, setDOff] = useState<[number, number]>([0, 0]);
  const [dW, setDW] = useState(0);
  const drag = usePointerDrag(
    scale,
    useCallback(
      (dx, dy, phase) => {
        let adx = dx;
        let ady = dy;
        if (snap) {
          const s = snap([x0 + dx, y0 + dy, x1 + dx, y1 + dy], phase);
          adx += s.dx;
          ady += s.dy;
        }
        if (phase === "move") {
          setDOff([adx, ady]);
        } else if (phase === "end" || phase === "cancel") {
          setDOff([0, 0]);
          if (phase === "end" && (Math.abs(adx) > 1 || Math.abs(ady) > 1)) {
            onMove([x0 + adx, y0 + ady, x1 + adx, y1 + ady]);
          }
        }
      },
      [x0, y0, x1, y1, onMove, snap]
    )
  );
  // An image resizes aspect-LOCKED from any of its four corners —
  // squashing a photo is almost never what someone meant. See
  // corner-resize.ts.
  const [grab, setGrab] = useState<{ dx: number; corner: Corner } | null>(null);
  const preview: Box = grab
    ? resizeLocked([x0, y0, x1, y1], grab.corner, grab.dx)
    : [x0, y0, x1, y1];
  const [prevX, prevY] = preview;
  const prevW = preview[2] - preview[0];
  const prevH = preview[3] - preview[1];
  return (
    <div
      className="pe-imgsel z-30"
      style={{
        left: (prevX + dOff[0]) * scale,
        top: (prevY + dOff[1]) * scale,
        width: prevW * scale,
        height: prevH * scale,
        cursor: busy ? "progress" : "move",
      }}
      onPointerDown={
        busy ? undefined : (e) => !isMultiSelectClick(e) && drag.onPointerDown(e)
      }
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
    >
      <div className="pe-imgsel-tools" onPointerDown={(e) => e.stopPropagation()}>
        <button type="button" disabled={busy} title="Replace image" onClick={onReplace}>
          <i className="ri-exchange-line" aria-hidden />
        </button>
        <button type="button" disabled={busy} title="Delete image (Del)" onClick={onDelete}>
          <i className="ri-delete-bin-line" aria-hidden />
        </button>
      </div>
      {grab && (
        <HtmlBoxDims width={prevW} height={prevH} scale={scale} originX={prevX} originY={prevY} />
      )}
      {!busy &&
        ALL_CORNERS.map((corner) => (
          <CornerHandle
            key={corner}
            corner={corner}
            scale={scale}
            title="Resize (keeps the aspect ratio)"
            onResize={(dx, _dy, phase) => {
              if (phase === "move") setGrab({ dx, corner });
              else if (phase === "cancel") setGrab(null);
              else if (phase === "end") {
                setGrab(null);
                const next = resizeLocked([x0, y0, x1, y1], corner, dx);
                if (boxChanged([x0, y0, x1, y1], next)) onMove(next);
              }
            }}
          />
        ))}
    </div>
  );
});

/** Move/scale handles for the open text edit: a grip on the left drags
 *  the text to a new spot, the corner knob scales the font size. Both
 *  are LIVE patches — the erase-and-redraw happens at commit, so a
 *  move plus a retype is still one undo step. preventDefault keeps the
 *  input focused (a blur would commit mid-drag). */
export function TextEditHandles({
  scale,
  edit,
  onPatch,
  left,
  top,
  width,
  height,
}: {
  scale: number;
  edit: ActiveTextEdit;
  onPatch: (
    patch: Partial<Pick<ActiveTextEdit, "moveDx" | "moveDy" | "size">>
  ) => void;
  left: number;
  top: number;
  width: number;
  height: number;
}) {
  const base = useRef({ dx: 0, dy: 0, size: 12, w: 100 });
  const move = usePointerDrag(
    scale,
    useCallback(
      (dx, dy, phase) => {
        if (phase === "move" || phase === "end") {
          onPatch({ moveDx: base.current.dx + dx, moveDy: base.current.dy + dy });
        } else if (phase === "cancel") {
          onPatch({ moveDx: base.current.dx, moveDy: base.current.dy });
        }
      },
      [onPatch]
    )
  );
  const resize = usePointerDrag(
    scale,
    useCallback(
      (dx, _dy, phase) => {
        if (phase === "move" || phase === "end") {
          const k = Math.max(0.2, Math.min(8, (base.current.w + dx) / base.current.w));
          onPatch({ size: Math.max(3, Math.min(240, base.current.size * k)) });
        } else if (phase === "cancel") {
          onPatch({ size: base.current.size });
        }
      },
      [onPatch]
    )
  );
  return (
    <>
      <div
        className="pe-textedit-grip z-30"
        title="Drag to move the text"
        style={{ left: left - 20, top, height: Math.max(24, height) }}
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          base.current = { ...base.current, dx: edit.moveDx, dy: edit.moveDy };
          move.onPointerDown(e);
        }}
        onPointerMove={move.onPointerMove}
        onPointerUp={move.onPointerUp}
        onPointerCancel={move.onPointerCancel}
      >
        <i className="ri-draggable" aria-hidden />
      </div>
      <div
        className="pe-stamp-handle z-30"
        title="Drag to scale the text"
        style={{ left: left + width - 5, top: top + height - 5, right: "auto", bottom: "auto" }}
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          base.current = {
            ...base.current,
            size: edit.size,
            w: Math.max(
              8,
              (edit.bbox[2] - edit.bbox[0]) *
                (edit.origSize > 0 ? edit.size / edit.origSize : 1)
            ),
          };
          resize.onPointerDown(e);
        }}
        onPointerMove={resize.onPointerMove}
        onPointerUp={resize.onPointerUp}
        onPointerCancel={resize.onPointerCancel}
      />
    </>
  );
}

/** A multi-object selection: per-object outlines inside one draggable
 *  frame. The set can mix page content, form fields and annotations —
 *  dragging anywhere in the frame moves all of them, and the whole
 *  group commits as one edit. */
export const RegionSelView = memo(function RegionSelView({
  items,
  scale,
  busy,
  snap,
  label,
  preview,
  onMove,
  onTapAt,
}: {
  items: RegionItem[];
  scale: number;
  busy: boolean;
  snap?: ContentSnap;
  /** "2 texts, 1 field" — built from the item kinds. */
  label: string;
  /** Un-committed arrow-key nudge, drawn while the keys are still
   *  going (the commit is debounced — one rewrite, not twenty). */
  preview?: [number, number];
  onMove: (dx: number, dy: number) => void;
  /** Click without a drag: collapse to the object under the pointer. */
  onTapAt?: (x: number, y: number) => void;
}) {
  const [off, setOff] = useState<[number, number]>([0, 0]);
  const x0 = Math.min(...items.map((i) => i.bbox[0]));
  const y0 = Math.min(...items.map((i) => i.bbox[1]));
  const x1 = Math.max(...items.map((i) => i.bbox[2]));
  const y1 = Math.max(...items.map((i) => i.bbox[3]));
  const drag = usePointerDrag(
    scale,
    useCallback(
      (dx, dy, phase) => {
        let adx = dx;
        let ady = dy;
        if (snap) {
          const s = snap([x0 + dx, y0 + dy, x1 + dx, y1 + dy], phase);
          adx += s.dx;
          ady += s.dy;
        }
        if (phase === "move") {
          setOff([adx, ady]);
        } else if (phase === "end" || phase === "cancel") {
          setOff([0, 0]);
          if (phase === "end" && (Math.abs(adx) > 1 || Math.abs(ady) > 1)) onMove(adx, ady);
        }
      },
      [onMove, snap, x0, y0, x1, y1]
    )
  );
  const [px, py] = preview ?? [0, 0];
  const dx = off[0] + px;
  const dy = off[1] + py;
  // A press that never became a drag collapses the group to one object.
  const tapStart = useRef<{ x: number; y: number } | null>(null);
  return (
    <div
      className="pe-regionsel z-30"
      title={`${label} selected — drag to move them together; Ctrl/Cmd+click (or Shift+click) an object to add or remove it`}
      style={{
        left: (x0 + dx) * scale - 4,
        top: (y0 + dy) * scale - 4,
        width: (x1 - x0) * scale + 8,
        height: (y1 - y0) * scale + 8,
        cursor: busy ? "progress" : "move",
      }}
      onPointerDown={
        busy
          ? undefined
          : (e) => {
              if (isMultiSelectClick(e)) return;
              tapStart.current = { x: e.clientX, y: e.clientY };
              drag.onPointerDown(e);
            }
      }
      onPointerMove={drag.onPointerMove}
      onPointerUp={(e) => {
        const t = tapStart.current;
        tapStart.current = null;
        drag.onPointerUp(e);
        if (!t || !onTapAt) return;
        if (Math.abs(e.clientX - t.x) > 3 || Math.abs(e.clientY - t.y) > 3) return;
        const r = e.currentTarget.getBoundingClientRect();
        onTapAt(
          x0 - 4 / scale + (e.clientX - r.left) / scale,
          y0 - 4 / scale + (e.clientY - r.top) / scale
        );
      }}
      onPointerCancel={() => {
        // Not a tap — the gesture was taken away, not completed.
        tapStart.current = null;
        drag.onPointerCancel();
      }}
    >
      {items.map((it) => (
        <div
          key={it.id}
          className="pe-regionsel-item"
          data-kind={it.kind}
          style={{
            left: (it.bbox[0] - x0) * scale + 4,
            top: (it.bbox[1] - y0) * scale + 4,
            width: (it.bbox[2] - it.bbox[0]) * scale,
            height: (it.bbox[3] - it.bbox[1]) * scale,
          }}
        />
      ))}
      <span className="pe-regionsel-badge">{label}</span>
    </div>
  );
});

/** A selected vector fill block: dashed frame; rect fills
 *  drag-move and corner-resize (free), everything gets recolour +
 *  delete. Each commit is an erase-and-redraw content edit. */
export const FillSelView = memo(function FillSelView({
  fill,
  scale,
  busy,
  snap,
  onMove,
  onDelete,
  onRecolor,
  onRestack,
  onGradientStops,
  onGradientReverse,
}: {
  fill: FillInfo;
  scale: number;
  busy: boolean;
  snap?: ContentSnap;
  onMove: (to: [number, number, number, number]) => void;
  onDelete: () => void;
  onRecolor: (hex: string) => void;
  /** Bring to front / send to back (recolourable fills only). */
  onRestack?: (front: boolean) => void;
  /** Gradient edits (located gradients only). */
  onGradientStops?: (stops: { start?: string; end?: string }) => void;
  onGradientReverse?: () => void;
}) {
  const [x0, y0, x1, y1] = fill.bbox;
  const w = x1 - x0;
  const h = y1 - y0;
  const movable = fill.rect && !busy;
  const [dOff, setDOff] = useState<[number, number]>([0, 0]);
  const [dSz, setDSz] = useState<[number, number]>([0, 0]);
  const [color, setColor] = useState(fill.color);
  useEffect(() => setColor(fill.color), [fill.id, fill.color]);
  const gm = fill.gradientMeta;
  const [gStart, setGStart] = useState(gm?.start ?? "#888888");
  const [gEnd, setGEnd] = useState(gm?.end ?? "#888888");
  useEffect(() => {
    setGStart(gm?.start ?? "#888888");
    setGEnd(gm?.end ?? "#888888");
  }, [fill.id, gm?.start, gm?.end]);
  const drag = usePointerDrag(
    scale,
    useCallback(
      (dx, dy, phase) => {
        let adx = dx;
        let ady = dy;
        if (snap) {
          const s = snap([x0 + dx, y0 + dy, x1 + dx, y1 + dy], phase);
          adx += s.dx;
          ady += s.dy;
        }
        if (phase === "move") {
          setDOff([adx, ady]);
        } else if (phase === "end" || phase === "cancel") {
          setDOff([0, 0]);
          if (phase === "end" && (Math.abs(adx) > 1 || Math.abs(ady) > 1)) {
            onMove([x0 + adx, y0 + ady, x1 + adx, y1 + ady]);
          }
        }
      },
      [x0, y0, x1, y1, onMove, snap]
    )
  );
  // Corner resizing: the arithmetic lives in corner-resize.ts, tested
  // there, and is shared with images and form fields.
  const [drag2, setDrag2] = useState<{ dx: number; dy: number; corner: Corner } | null>(null);
  const box: Box = drag2
    ? resizeFree([x0, y0, x1, y1], drag2.corner, drag2.dx, drag2.dy)
    : [x0, y0, x1, y1];
  return (
    <div
      className="pe-imgsel z-30"
      style={{
        left: (box[0] + dOff[0]) * scale,
        top: (box[1] + dOff[1]) * scale,
        width: (box[2] - box[0]) * scale,
        height: (box[3] - box[1]) * scale,
        cursor: busy ? "progress" : movable ? "move" : "default",
      }}
      title={
        fill.gradient
          ? "Gradient — edit its stop colours, reverse or delete"
          : fill.stroke
            ? "Border (stroked path)"
            : fill.partOfGroup
              ? "Colour block — one piece of a multi-part object"
              : "Colour block"
      }
      onPointerDown={
        movable ? (e) => !isMultiSelectClick(e) && drag.onPointerDown(e) : undefined
      }
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
    >
      <div className="pe-imgsel-tools" onPointerDown={(e) => e.stopPropagation()}>
        {fill.recolorable && !busy && (
          <ColorPicker
            value={color}
            onChange={setColor}
            onCommit={(hex) => {
              if (hex.toLowerCase() !== fill.color.toLowerCase()) onRecolor(hex);
            }}
            title={fill.stroke ? "Border colour" : "Block colour"}
          />
        )}
        {fill.gradient && gm?.stopsEditable && !busy && (
          <>
            <ColorPicker
              value={gStart}
              onChange={setGStart}
              onCommit={(hex) => {
                if (hex.toLowerCase() !== (gm.start ?? "").toLowerCase()) {
                  onGradientStops?.({ start: hex });
                }
              }}
              title="Gradient start colour"
            />
            <ColorPicker
              value={gEnd}
              onChange={setGEnd}
              onCommit={(hex) => {
                if (hex.toLowerCase() !== (gm.end ?? "").toLowerCase()) {
                  onGradientStops?.({ end: hex });
                }
              }}
              title="Gradient end colour"
            />
          </>
        )}
        {fill.gradient && gm?.reversible && (
          <button
            type="button"
            disabled={busy}
            title="Reverse the gradient direction"
            onClick={() => onGradientReverse?.()}
          >
            <i className="ri-arrow-left-right-line" aria-hidden />
          </button>
        )}
        {fill.recolorable && onRestack && (
          <>
            <button
              type="button"
              disabled={busy}
              title="Bring to front — paints above everything on the page, text included"
              onClick={() => onRestack(true)}
            >
              <i className="ri-bring-to-front" aria-hidden />
            </button>
            <button
              type="button"
              disabled={busy}
              title="Send to back — paints behind all other page content"
              onClick={() => onRestack(false)}
            >
              <i className="ri-send-to-back" aria-hidden />
            </button>
          </>
        )}
        <button
          type="button"
          disabled={busy || (fill.gradient && !gm?.located)}
          title={fill.gradient ? "Delete gradient (Del)" : "Delete block (Del)"}
          onClick={onDelete}
        >
          <i className="ri-delete-bin-line" aria-hidden />
        </button>
      </div>
      {drag2 && (
        <HtmlBoxDims
          width={box[2] - box[0]}
          height={box[3] - box[1]}
          scale={scale}
          originX={box[0]}
          originY={box[1]}
        />
      )}
      {movable &&
        ALL_CORNERS.map((corner) => (
          <CornerHandle
            key={corner}
            corner={corner}
            scale={scale}
            onResize={(dx, dy, phase) => {
              if (phase === "move") setDrag2({ dx, dy, corner });
              else if (phase === "cancel") setDrag2(null);
              else if (phase === "end") {
                const next = resizeFree([x0, y0, x1, y1], corner, dx, dy);
                setDrag2(null);
                if (boxChanged([x0, y0, x1, y1], next)) onMove(next);
              }
            }}
          />
        ))}
    </div>
  );
});

// ─────────────────────────── form widgets ───────────────────────────

export function fieldFontPx(heightPx: number): number {
  return Math.max(9, Math.min(heightPx * 0.55, 16));
}

/** One AcroForm field as an HTML input overlaid on the page raster.
 *  The input's opaque background hides the appearance MuPDF rendered
 *  underneath, so the value the user sees is always the live one. */
export const FormWidgetView = memo(function FormWidgetView({
  w,
  value,
  scale,
  interactive,
  onChange,
  onMoveField,
}: {
  w: WidgetInfo;
  value: string;
  scale: number;
  interactive: boolean;
  onChange: (name: string, value: string) => void;
  /** Move or resize this field's box. Absent = the document can't take
   *  it (no field tool armed, or a busy rebase). */
  onMoveField?: (rect: [number, number, number, number], phase: DragPhase) => void;
}) {
  const [x0, y0, x1, y1] = w.rect;
  // A field is a form control FIRST: dragging it about must never be
  // something you can do by accident while filling it in. So the grip
  // and the corners only exist while the Field tool is armed — the same
  // rule that decides whether a link is followable or draggable.
  const editing = !!onMoveField;
  const [dOff, setDOff] = useState<[number, number]>([0, 0]);
  const [grab, setGrab] = useState<{ dx: number; dy: number; corner: Corner } | null>(null);
  // A field smaller than 8pt is unclickable, so that is its floor.
  const box: Box = grab
    ? resizeFree([x0, y0, x1, y1], grab.corner, grab.dx, grab.dy, 8)
    : [x0 + dOff[0], y0 + dOff[1], x1 + dOff[0], y1 + dOff[1]];
  const moveDrag = usePointerDrag(
    scale,
    useCallback(
      (dx, dy, phase) => {
        if (!onMoveField) return;
        if (phase === "move") setDOff([dx, dy]);
        else if (phase === "cancel") setDOff([0, 0]);
        else if (phase === "end") {
          setDOff([0, 0]);
          if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
            onMoveField([x0 + dx, y0 + dy, x1 + dx, y1 + dy], "end");
          }
        }
      },
      [onMoveField, x0, y0, x1, y1]
    )
  );
  const style: React.CSSProperties = {
    left: box[0] * scale,
    top: box[1] * scale,
    width: (box[2] - box[0]) * scale,
    height: (box[3] - box[1]) * scale,
    pointerEvents: (interactive && !w.readOnly) || editing ? "auto" : "none",
  };
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

  if (editing) {
    // In field-editing mode the widget is a BOX, not a control: a plain
    // outline you drag, with corners to resize. Rendering the real input
    // here would invite typing into something that is about to move.
    return (
      <div
        className="pe-imgsel z-20"
        style={{ ...style, cursor: "move" }}
        title={`${w.name} — drag to move, corners to resize`}
        onPointerDown={moveDrag.onPointerDown}
        onPointerMove={moveDrag.onPointerMove}
        onPointerUp={moveDrag.onPointerUp}
        onPointerCancel={moveDrag.onPointerCancel}
      >
        <span className="pointer-events-none absolute left-1 top-0 truncate text-[10px] leading-tight text-[color:var(--color-accent)]">
          {w.name}
        </span>
        {(grab || moveDrag.dragging) && (
          <HtmlBoxDims
            width={box[2] - box[0]}
            height={box[3] - box[1]}
            scale={scale}
            originX={box[0]}
            originY={box[1]}
          />
        )}
        {ALL_CORNERS.map((corner) => (
          <CornerHandle
            key={corner}
            corner={corner}
            scale={scale}
            onResize={(dx, dy, phase) => {
              if (phase === "move") setGrab({ dx, dy, corner });
              else if (phase === "cancel") setGrab(null);
              else if (phase === "end") {
                const next = resizeFree([x0, y0, x1, y1], corner, dx, dy, 8);
                setGrab(null);
                if (boxChanged(w.rect as Box, next)) onMoveField(next, "end");
              }
            }}
          />
        ))}
      </div>
    );
  }

  if (w.type === "checkbox" || w.type === "radio") {
    const on = w.onValue ?? "Yes";
    return (
      <input
        type={w.type === "radio" ? "radio" : "checkbox"}
        className="pe-widget pe-widget-check z-10"
        style={style}
        checked={value === on}
        disabled={w.readOnly}
        title={w.name}
        onPointerDown={stop}
        onChange={() =>
          onChange(w.name, w.type === "radio" ? on : value === on ? "Off" : on)
        }
      />
    );
  }
  if (w.type === "sig") {
    // An unsigned signature field: a designated place to sign. Not
    // fillable in this editor (File → "Digitally sign…" signs the whole
    // document), so the overlay only shows that the field is there —
    // otherwise a field the user just created would look like it
    // vanished, while still appearing in every other reader.
    return (
      <div
        className="pe-widget z-10 flex items-center justify-center gap-1 border border-dashed border-[color:var(--color-fg-muted)] bg-transparent text-[color:var(--color-fg-muted)]"
        style={{ ...style, pointerEvents: "none", fontSize: Math.max(8, 10 * scale) }}
        title={`${w.name} — signature field (sign it in any PDF reader)`}
      >
        <i className="ri-quill-pen-line" aria-hidden />
        Signature
      </div>
    );
  }
  if (w.type === "combo" || w.type === "list") {
    const opts = w.options ?? [];
    return (
      <select
        className="pe-widget z-10"
        style={{ ...style, fontSize: fieldFontPx((y1 - y0) * scale) }}
        value={value}
        disabled={w.readOnly}
        title={w.name}
        onPointerDown={stop}
        onChange={(e) => onChange(w.name, e.target.value)}
      >
        {!opts.includes(value) && <option value={value}>{value}</option>}
        {opts.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (w.multiline) {
    return (
      <textarea
        className="pe-widget pe-widget-multiline z-10"
        style={{ ...style, fontSize: w.fontSize ? w.fontSize * scale : Math.max(9, 11 * scale) }}
        value={value}
        disabled={w.readOnly}
        maxLength={w.maxLen}
        title={w.name}
        onPointerDown={stop}
        onChange={(e) => onChange(w.name, e.target.value)}
      />
    );
  }
  return (
    <input
      type={w.password ? "password" : "text"}
      className="pe-widget pe-widget-text z-10"
      style={{
        ...style,
        fontSize: w.fontSize ? w.fontSize * scale : fieldFontPx((y1 - y0) * scale),
      }}
      value={value}
      disabled={w.readOnly}
      maxLength={w.maxLen}
      title={w.name}
      onPointerDown={stop}
      onChange={(e) => onChange(w.name, e.target.value)}
    />
  );
});
